// Notifications Panel for displaying user notifications
import { db, auth } from '../firebase.js';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';

export class NotificationsPanel {
  constructor() {
    this.notifications = [];
    this.unsubscribe = null;
  }

  async open() {
    const modal = document.createElement('div');
    modal.className = 'notifications-modal-overlay';
    modal.innerHTML = `
      <div class="notifications-modal">
        <div class="modal-header">
          <h2>🔔 Notifications</h2>
          <button class="close-btn">✕</button>
        </div>
        <div id="notifications-list" class="notifications-list">
          <div class="loading">Chargement...</div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const closeBtn = modal.querySelector('.close-btn');
    closeBtn.addEventListener('click', () => {
      modal.remove();
      if (this.unsubscribe) this.unsubscribe();
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
        if (this.unsubscribe) this.unsubscribe();
      }
    });

    this.loadNotifications(modal);
  }

  loadNotifications(modal) {
    if (!auth.currentUser) return;

    const notifQuery = query(
      collection(db, 'notifications'),
      where('recipientId', '==', auth.currentUser.uid),
      orderBy('timestamp', 'desc')
    );

    const listContainer = modal.querySelector('#notifications-list');

    this.unsubscribe = onSnapshot(notifQuery, (snapshot) => {
      listContainer.innerHTML = '';

      if (snapshot.empty) {
        listContainer.innerHTML = '<div class="no-notifications">Aucune notification</div>';
        return;
      }

      snapshot.docs.forEach(doc => {
        const notif = doc.data();
        const notifEl = this.createNotificationElement(doc.id, notif);
        listContainer.appendChild(notifEl);
      });
    }, (error) => {
      console.error('Erreur notifications:', error);
      listContainer.innerHTML = '<div class="no-notifications">Erreur de chargement</div>';
    });
  }

  createNotificationElement(notifId, notif) {
    const notifEl = document.createElement('div');
    notifEl.className = `notification-item ${notif.read ? '' : 'unread'}`;
    notifEl.dataset.notifId = notifId;

    notifEl.innerHTML = `
      <div class="notif-content">
        <img src="${notif.senderAvatar || '/default-avatar.png'}" alt="Avatar" class="notif-avatar">
        <div class="notif-text">
          <p class="notif-message">
            <strong>${notif.senderName}</strong> 
            ${this.getNotificationMessage(notif.type)}
          </p>
          <span class="notif-time">${this.formatTime(notif.timestamp)}</span>
        </div>
      </div>
      ${notif.type === 'mention' ? `
        <div class="notif-mentioned-text">
          <em>"${notif.postText || notif.verseText}..."</em>
        </div>
      ` : ''}
      <button class="notif-close" data-notif-id="${notifId}">✕</button>
    `;

    const closeBtn = notifEl.querySelector('.notif-close');
    closeBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      await this.deleteNotification(notifId);
    });

    notifEl.addEventListener('click', async () => {
      await updateDoc(doc(db, 'notifications', notifId), { read: true });
      notifEl.classList.remove('unread');
    });

    return notifEl;
  }

  getNotificationMessage(type) {
    const messages = {
      mention: 'vous a mentionné',
      like: 'a aimé votre publication',
      comment: 'a commenté votre publication',
      reaction: 'a réagi à votre publication'
    };
    return messages[type] || 'nouvelle notification';
  }

  formatTime(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    return `Il y a ${Math.floor(diff / 86400000)}j`;
  }

  async deleteNotification(notifId) {
    try {
      await deleteDoc(doc(db, 'notifications', notifId));
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  }

  static async sendNotification(data) {
    try {
      await addDoc(collection(db, 'notifications'), {
        recipientId: data.recipientId,
        senderId: data.senderId,
        senderName: data.senderName,
        senderAvatar: data.senderAvatar,
        type: data.type,
        postId: data.postId,
        verseText: data.verseText,
        postText: data.postText,
        read: false,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
    }
  }
}
